import React, {FormEvent, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle, FormControl, InputLabel, MenuItem,
	Paper, Select,
	Stack,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {
	entityStatusColumn,
	productIncentiveTypeColumn,
	textColumn,
	tooltipColumn
} from "../../../components/common/grid/Columns";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {PRODUCT_INCENTIVE_OVERVIEW, PRODUCT_INCENTIVE_SUMMARY} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {productsEditorDefaultTargetStage} from "../ProductModuleOverview";
import {
	linkProductIncentiveDetailsFor,
	openProductIncentiveDetailsForNew
} from "./IncentiveDetails";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {productIncentiveTypes} from "../models/ProductIncentiveType";
import {ProductIncentiveDetailsKrn} from "../../../libraries/krn/Krn";
import {translateFunction} from "../../../localization/TranslationService";
import {ProductIncentiveDetailsSummary} from "../models/ProductIncentiveDetailsSummary";
import {useQuery} from "@apollo/client";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {Configuration} from "../../../global/Configuration";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const IncentiveOverview = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Typography color="text.primary">{translate("Incentives")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<ProductsIncentiveOverviewContent/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

export function ProductsIncentiveOverviewContent() {
	const [summaryLookup, setSummaryLookup] = useState<Map<string, ProductIncentiveDetailsSummary>>();
	
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params: any) => (
				<Link to={linkProductIncentiveDetailsFor(params.row.krn)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
		},
		{
			field: "krn",
			headerName: translate("Kizi Resource Name"),
			minWidth: 300,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "totalCodes",
			headerName: translate("Total Codes"),
			minWidth: 50,
			renderCell: (params: any) => (
				getTotalCodes(params.row.krn, summaryLookup)
			),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "availableCodes",
			headerName: translate("Available Codes"),
			minWidth: 50,
			renderCell: (params: any) => (
				getAvailableCount(params.row.krn, summaryLookup)
			),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productIncentiveType",
			headerName: translate("Product Incentive Type"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: productIncentiveTypeColumn(),
			valueOptions: productIncentiveTypes as string[],
			valueFormatter: (params: any) => formatProductIncentiveType(params.value),
			flex: 1,
		},
		{
			field: "label",
			headerName: translate("Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "state",
			headerName: translate("State"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: entityStatusColumn(),
			valueOptions: ["ACTIVE", "INACTIVE", "DELETED"],
			valueFormatter: (params: any) => formatEntityState(params.value),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			type: "dateTime",
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params: any) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params: any) => formatDateTimeShort(params.value),
			flex: 1,
		}
	];
	
	const incentiveSummaryQuery = useQuery(PRODUCT_INCENTIVE_SUMMARY, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {}
	});
	
	if (!incentiveSummaryQuery.loading && !summaryLookup) {
		const nodes: ProductIncentiveDetailsSummary[] = incentiveSummaryQuery.data.productIncentiveSummary;
		setSummaryLookup(nodes.associateBy(it => it.productIncentiveDetailsKrn, it => it));
	}
	
	return <GraphQlDataGrid
		rows={[]}
		query={PRODUCT_INCENTIVE_OVERVIEW}
		graphFunction={"productIncentiveDetails"}
		client={graphQLServices.productsClient}
		rowsPerPageOptions={[10, 25, 50]}
		initialPageSize={50}
		columns={columns}
		getRowId={row => row.krn}
		defaultSortModel={[
			{
				field: "krn",
				sort: "asc"
			}
		]}
		defaultFilterModel={{
			items: [
				{
					field: "state",
					value: ["ACTIVE", "INACTIVE"],
					operator: "in",
				},
			]
		}}
		slots={{
			footer: CustomFooter
		}}
		queryVariables={{stage: GlobalState.targetKiziStage(productsEditorDefaultTargetStage)}}
	/>;
}

function CustomFooter(props: any) {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	
	
	const handleClickOpen = () => {
		setOpen(true);
		setError("");
	};
	
	const handleClose = () => {
		setOpen(false);
	};
	
	const handleCreate = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		
		const data = new FormData(event.target as HTMLFormElement);
		const clientName = data.get("clientName") as string;
		const incentiveType = data.get("incentiveType") as string;
		const krn = new ProductIncentiveDetailsKrn(clientName, incentiveType);
		setOpen(false);
		openProductIncentiveDetailsForNew(krn.fullKrn);
	};
	
	return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
		padding: "0 10px"
	}}>
		<Button
			variant={"contained"}
			onClick={handleClickOpen}
		>{translate("Add")}</Button>
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{translate("New Incentive")}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{translate("Please provide the client name and incentive type.")}
				</DialogContentText>
				<form onSubmit={handleCreate}>
					<FormControl
						margin={"dense"}
						error={!!error}
						fullWidth>
						<InputLabel id={"clientName"}>{translate("Client Name")}</InputLabel>
						<Select
							name={"clientName"}
							error={!!error}
							labelId={"clientName"}
							label={translate("Client Name")}
							variant={"standard"}
							required
						>
							<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{Configuration.clientNames.map(it =>
								<MenuItem key={it.toLowerCase()} value={it}>{translate(it)}</MenuItem>)}
						</Select>
					</FormControl>
					<FormControl
						margin={"dense"}
						error={!!error}
						fullWidth>
						<InputLabel id={"incentiveType"}>{translate("Incentive Type")}</InputLabel>
						<Select
							name={"incentiveType"}
							error={!!error}
							labelId={"incentiveType"}
							label={translate("Incentive Type")}
							variant={"standard"}
							required
						>
							<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{productIncentiveTypes.map(it =>
								<MenuItem key={it.toLowerCase()} value={it}>{translate(it)}</MenuItem>)}
						</Select>
					</FormControl>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						<Button onClick={handleClose}>{translate("Cancel")}</Button>
						<Button type={"submit"} color={"success"}>{translate("Create")}</Button>
					</Stack>
				</form>
			</DialogContent>
		</Dialog>
		<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
	</Stack>;
}

function formatProductIncentiveType(text: string | null): string {
	return formatEnum("productIncentiveType", text);
}

function formatEntityState(text: string | null): string {
	return formatEnum("entityState", text);
}

function formatEnum(prefix: string, text: string | null): string {
	if (!text)
		return "";
	return translate(text)
		?? translate("Unknown");
}

function getTotalCodes(id: string, items: Map<string, ProductIncentiveDetailsSummary> | undefined): string {
	return items?.get(id)?.totalCodes.toString() ?? "0";
}

function getAvailableCount(id: string, items: Map<string, ProductIncentiveDetailsSummary> | undefined): string {
	return items?.get(id)?.availableCodes.toString() ?? "0";
}
